import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import './Home.css';

interface MainData {
  id: number;
  background_image: string | null;
  header: string;
  text: string;
  created_at: string;
  updated_at: string;
}

interface MissionData {
  id: number;
  image: string | null;
  header: string;
  text: string;
  happy_guests: number;
  returning_guests: number;
  satisfied_guests: number;
  reviews: number;
  created_at: string;
  updated_at: string;
}

interface DiscoverData {
  id: number;
  header: string;
  text: string;
  image: string | null;
  features: Array<{
    id: number;
    description: string;
    icon: string | null;
    created_at: string;
  }>;
  created_at: string;
  updated_at: string;
}

interface GalleryImage {
  id: number;
  image: string | null;
  alt_text: string;
  order: number;
  created_at: string;
}

interface GalleryData {
  id: number;
  header: string;
  description: string;
  images: GalleryImage[];
  created_at: string;
  updated_at: string;
}

interface ChooseData {
  id: number;
  header: string;
  sc1_header: string;
  sc1_description: string;
  sc1_avatar: string | null;
  sc2_header: string;
  sc2_description: string;
  sc2_avatar: string | null;
  sc3_header: string;
  sc3_description: string;
  sc3_avatar: string | null;
  created_at: string;
  updated_at: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [mainData, setMainData] = useState<MainData | null>(null);
  const [missionData, setMissionData] = useState<MissionData | null>(null);
  const [discoverData, setDiscoverData] = useState<DiscoverData | null>(null);
  const [galleryData, setGalleryData] = useState<GalleryData | null>(null);
  const [chooseData, setChooseData] = useState<ChooseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Home | Patates Guesthouse';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch main data
        const mainResponse = await fetch(`${API_BASE_URL}/main/`);
        if (!mainResponse.ok) {
          throw new Error('Failed to fetch main data');
        }
        const mainData = await mainResponse.json();
        setMainData(mainData);

        // Fetch mission data
        const missionResponse = await fetch(`${API_BASE_URL}/mission/`);
        if (!missionResponse.ok) {
          throw new Error('Failed to fetch mission data');
        }
        const missionData = await missionResponse.json();
        setMissionData(missionData);

        // Fetch discover data
        const discoverResponse = await fetch(`${API_BASE_URL}/discover/`);
        if (!discoverResponse.ok) {
          throw new Error('Failed to fetch discover data');
        }
        const discoverData = await discoverResponse.json();
        setDiscoverData(discoverData);

        // Fetch gallery data
        const galleryResponse = await fetch(`${API_BASE_URL}/gallery/`);
        if (!galleryResponse.ok) {
          throw new Error('Failed to fetch gallery data');
        }
        const galleryData = await galleryResponse.json();
        setGalleryData(galleryData);

        // Fetch choose data
        const chooseResponse = await fetch(`${API_BASE_URL}/choose/`);
        if (!chooseResponse.ok) {
          throw new Error('Failed to fetch choose data');
        }
        const chooseData = await chooseResponse.json();
        setChooseData(chooseData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="home">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home">
        <div className="error">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      {/* Hero Section */}
      <section 
        className="hero" 
        style={{
          backgroundImage: mainData?.background_image 
            ? `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${mainData.background_image})`
            : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="hero-content">
          <h1>{mainData?.header || 'Welcome to Patates'}</h1>
          <p>{mainData?.text || 'Your perfect getaway destination'}</p>
          <div className="booking-buttons">
            <a 
              href="https://www.booking.com/hotel/am/your-villa-with-garden-in-martuni.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cta-button booking-button"
            >
              Booking.com
            </a>
            <a 
              href="https://airbnb.com/h/martuni" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cta-button airbnb-button"
            >
              Airbnb
            </a>
          </div>
        </div>
      </section>

      {/* Our Story & Mission Section */}
      <section className="story-mission">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2>{missionData?.header || 'Our Story & Mission'}</h2>
              <p>{missionData?.text || 'At Your Home, we are dedicated to offering a cozy retreat near the stunning Lake Sevan. With a rich history rooted in hospitality, our mission is to provide memorable experiences through comfort, natural beauty, and personalized service.'}</p>
              
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-number">{missionData?.happy_guests || 150} +</div>
                  <div className="stat-label">happy guests</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{missionData?.returning_guests || 50} +</div>
                  <div className="stat-label">returning guests</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{missionData?.satisfied_guests || 200} +</div>
                  <div className="stat-label">satisfied visitors</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{missionData?.reviews || 300} +</div>
                  <div className="stat-label">positive reviews</div>
                </div>
              </div>
            </div>
            <div className="story-image">
              {missionData?.image ? (
                <img 
                  src={missionData.image} 
                  alt="Mission Image" 
                  className="mission-image"
                />
              ) : (
                <div className="image-placeholder">🏞️</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lake Sevan Section */}
      <section className="lake-sevan">
        <div className="container">
          <div className="lake-content">
            <div className="lake-text">
              <h2>{discoverData?.header || 'Discover the Beauty of Lake Sevan from Our Guesthouse'}</h2>
              <p>{discoverData?.text || 'Nestled near the shores of Lake Sevan, Your home offers a unique opportunity to experience the natural beauty of Armenia. Our location provides easy access to the lake\'s stunning views and recreational activities, making your stay unforgettable.'}</p>
              
              <div className="lake-features">
                {discoverData?.features && discoverData.features.length > 0 ? (
                  discoverData.features.map((feature, index) => (
                    <div key={feature.id} className="lake-feature">
                      <div className="feature-icon">
                        {feature.icon ? (
                          <img src={feature.icon} alt="Feature icon" style={{ width: '24px', height: '24px' }} />
                        ) : (
                          '📍'
                        )}
                      </div>
                      <div className="feature-text">
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="lake-feature">
                      <div className="feature-icon">📍</div>
                      <div className="feature-text">
                        <p>Enjoy easy access to the lake's beaches and water activities.</p>
                      </div>
                    </div>
                    <div className="lake-feature">
                      <div className="feature-icon">🏔️</div>
                      <div className="feature-text">
                        <p>Wake up to breathtaking views of the lake and surrounding mountains.</p>
                      </div>
                    </div>
                    <div className="lake-feature">
                      <div className="feature-icon">🌿</div>
                      <div className="feature-text">
                        <p>Escape the city and unwind in our tranquil lakeside setting.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="lake-image">
              {discoverData?.image ? (
                <img 
                  src={discoverData.image} 
                  alt="Lake Sevan" 
                  className="discover-image"
                />
              ) : (
                <div className="image-placeholder">🏞️</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <h2 className="gallery-title">{galleryData?.header || 'Gallery'}</h2>
          <p className="gallery-description">{galleryData?.description || 'Explore our beautiful guesthouse with stunning photos of rooms and common areas.'}</p>
          <div className="gallery-grid">
            {galleryData?.images && galleryData.images.length > 0 ? (
              galleryData.images.slice(0, 6).map((image, index) => (
                <div key={image.id} className="gallery-item">
                  <img 
                    src={image.image || ''} 
                    alt={image.alt_text || `Gallery Image ${index + 1}`} 
                  />
                </div>
              ))
            ) : (
              <>
                <div className="gallery-item">
                  <img src="https://via.placeholder.com/400x300?text=Bedroom+View" alt="Bedroom View" />
                </div>
                <div className="gallery-item">
                  <img src="https://via.placeholder.com/400x300?text=Living+Room" alt="Living Room" />
                </div>
                <div className="gallery-item">
                  <img src="https://via.placeholder.com/400x300?text=Bedroom+2" alt="Bedroom 2" />
                </div>
                <div className="gallery-item">
                  <img src="https://via.placeholder.com/400x300?text=Kitchen" alt="Kitchen" />
                </div>
                <div className="gallery-item">
                  <img src="https://via.placeholder.com/400x300?text=Garden+View" alt="Garden View" />
                </div>
                <div className="gallery-item">
                  <img src="https://via.placeholder.com/400x300?text=Lake+View" alt="Lake View" />
                </div>
              </>
            )}
          </div>
          {galleryData?.images && galleryData.images.length > 6 && (
            <div className="gallery-button-container">
              <button 
                className="gallery-button"
                onClick={() => navigate('/gallery')}
              >
                More Photos
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2>{chooseData?.header || 'Why Choose Patates?'}</h2>
          <div className="features-grid">
            <div className="feature-card">
              {chooseData?.sc1_avatar && (
                <img 
                  src={chooseData.sc1_avatar} 
                  alt={chooseData.sc1_header}
                  className="feature-avatar"
                  style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '1rem' }}
                />
              )}
              <h3>{chooseData?.sc1_header || 'Comfortable Rooms'}</h3>
              <p>{chooseData?.sc1_description || 'Cozy and clean accommodations for your perfect stay'}</p>
            </div>
            <div className="feature-card">
              {chooseData?.sc2_avatar && (
                <img 
                  src={chooseData.sc2_avatar} 
                  alt={chooseData.sc2_header}
                  className="feature-avatar"
                  style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '1rem' }}
                />
              )}
              <h3>{chooseData?.sc2_header || 'Beautiful Views'}</h3>
              <p>{chooseData?.sc2_description || 'Stunning landscapes and breathtaking sunsets'}</p>
            </div>
            <div className="feature-card">
              {chooseData?.sc3_avatar && (
                <img 
                  src={chooseData.sc3_avatar} 
                  alt={chooseData.sc3_header}
                  className="feature-avatar"
                  style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '1rem' }}
                />
              )}
              <h3>{chooseData?.sc3_header || 'Delicious Food'}</h3>
              <p>{chooseData?.sc3_description || 'Fresh local cuisine prepared with love'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="location-section">
        <div className="container">
          <h2>Location</h2>
          <div className="map-container">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3050.31704056125!2d45.3349282!3d40.135221099999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x403fe5003d2f18c1%3A0xdfe772fef9267e84!2sPatates%20Guest%20House%20in%20Martuni!5e0!3m2!1sen!2sam!4v1753115050527!5m2!1sen!2sam" 
              width="100%" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen={true} 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Patates Guest House Location"
            ></iframe>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Home; 